import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Avatar,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../context/WorkspaceContext';

const WorkspaceSwitcher = () => {
  const {
    organizations,
    currentOrganization,
    currentTeam,
    teams,
    workspaceMode,
    setCurrentOrganization,
    setCurrentTeam,
    setWorkspaceMode,
    loading,
  } = useWorkspace();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [, setOrgSubMenuAnchor] = useState(null);
  const [, setSelectedOrgForTeams] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOrgSubMenuAnchor(null);
    setSelectedOrgForTeams(null);
  };

  const handlePersonalClick = () => {
    setWorkspaceMode('personal');
    handleClose();
  };

  const handleOrgClick = async (org) => {
    await setCurrentOrganization(org);
    handleClose();
  };

  const handleTeamClick = (team) => {
    setCurrentTeam(team);
    handleClose();
  };

  const handleManageOrgs = () => {
    handleClose();
    navigate('/organizations');
  };

  // Get display label
  const getDisplayLabel = () => {
    if (workspaceMode === 'team' && currentTeam) {
      return currentTeam.name;
    }
    if (workspaceMode === 'organization' && currentOrganization) {
      return currentOrganization.name;
    }
    return 'Personal';
  };

  // Get display icon
  const getDisplayIcon = () => {
    if (workspaceMode === 'team') {
      return <GroupIcon sx={{ mr: 1 }} />;
    }
    if (workspaceMode === 'organization') {
      if (currentOrganization?.logo) {
        return (
          <Avatar
            src={currentOrganization.logo}
            sx={{ width: 24, height: 24, mr: 1 }}
          />
        );
      }
      return <BusinessIcon sx={{ mr: 1 }} />;
    }
    return <PersonIcon sx={{ mr: 1 }} />;
  };

  if (loading) {
    return (
      <Button disabled sx={{ textTransform: 'none' }}>
        <PersonIcon sx={{ mr: 1 }} />
        Loading...
      </Button>
    );
  }

  return (
    <>
      <Button
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          textTransform: 'none',
          color: 'inherit',
          px: 2,
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
          },
        }}
      >
        {getDisplayIcon()}
        <Typography
          variant="body2"
          sx={{
            maxWidth: 150,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {getDisplayLabel()}
        </Typography>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: { minWidth: 250, maxWidth: 320 },
        }}
      >
        {/* Personal Workspace */}
        <MenuItem onClick={handlePersonalClick}>
          <ListItemIcon>
            <PersonIcon />
          </ListItemIcon>
          <ListItemText primary="Personal" secondary="Your personal sessions" />
          {workspaceMode === 'personal' && (
            <CheckIcon color="primary" sx={{ ml: 1 }} />
          )}
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        {/* Organizations */}
        {organizations.length > 0 && (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 2, py: 0.5, display: 'block' }}
            >
              Organizations
            </Typography>

            {organizations.map((org) => (
              <Box key={org.id}>
                <MenuItem
                  onClick={() => handleOrgClick(org)}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ListItemIcon>
                      {org.logo ? (
                        <Avatar src={org.logo} sx={{ width: 24, height: 24 }} />
                      ) : (
                        <BusinessIcon />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={org.name}
                      secondary={`${org.teams?.length || 0} teams`}
                    />
                  </Box>
                  {workspaceMode === 'organization' &&
                    currentOrganization?.id === org.id && (
                      <CheckIcon color="primary" />
                    )}
                </MenuItem>

                {/* Teams under this org (if it's the current org) */}
                {currentOrganization?.id === org.id && teams.length > 0 && (
                  <Box sx={{ pl: 4 }}>
                    {teams.map((team) => (
                      <MenuItem
                        key={team.id}
                        onClick={() => handleTeamClick(team)}
                        sx={{ pl: 2 }}
                      >
                        <ListItemIcon>
                          <GroupIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={team.name} />
                        {workspaceMode === 'team' &&
                          currentTeam?.id === team.id && (
                            <CheckIcon color="primary" />
                          )}
                      </MenuItem>
                    ))}
                  </Box>
                )}
              </Box>
            ))}

            <Divider sx={{ my: 1 }} />
          </>
        )}

        {/* Create/Manage Organizations */}
        <MenuItem onClick={handleManageOrgs}>
          <ListItemIcon>
            <AddIcon />
          </ListItemIcon>
          <ListItemText
            primary={
              organizations.length > 0
                ? 'Manage Organizations'
                : 'Create Organization'
            }
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default WorkspaceSwitcher;
